import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const KINDS = {
  publications: {
    label: "Εκδόσεις",
    titleField: "title",
    fields: [
      { name: "title", label: "Τίτλος", type: "text" },
      { name: "description", label: "Περιγραφή", type: "textarea" },
      { name: "category", label: "Κατηγορία", type: "text" },
      { name: "pages", label: "Σελίδες", type: "number" },
      { name: "featured", label: "Προβεβλημένη", type: "switch" },
      { name: "status", label: "Κατάσταση", type: "select", options: ["published", "draft"] },
    ],
  },
  tools: {
    label: "Ψηφιακά Εργαλεία",
    titleField: "name",
    fields: [
      { name: "name", label: "Όνομα", type: "text" },
      { name: "description", label: "Περιγραφή", type: "textarea" },
      { name: "tags", label: "Tags (χωρισμένα με κόμμα)", type: "text" },
      { name: "image", label: "URL εικόνας", type: "text" },
      { name: "status", label: "Κατάσταση", type: "select", options: ["published", "draft"] },
    ],
  },
  seminars: {
    label: "Σεμινάρια",
    titleField: "title",
    fields: [
      { name: "title", label: "Τίτλος", type: "text" },
      { name: "description", label: "Περιγραφή", type: "textarea" },
      { name: "date", label: "Ημερομηνία (π.χ. 14 Νοεμβρίου 2026)", type: "text" },
      { name: "duration", label: "Διάρκεια (π.χ. 6 ώρες)", type: "text" },
      { name: "format", label: "Μορφή", type: "select", options: ["Online", "Δια ζώσης"] },
      { name: "seats", label: "Θέσεις", type: "number" },
      { name: "status", label: "Κατάσταση", type: "select", options: ["published", "draft"] },
    ],
  },
  articles: {
    label: "Άρθρα",
    titleField: "title",
    fields: [
      { name: "title", label: "Τίτλος", type: "text" },
      { name: "excerpt", label: "Σύντομη περιγραφή", type: "textarea" },
      { name: "content", label: "Περιεχόμενο (κενή γραμμή = νέα παράγραφος)", type: "textarea" },
      { name: "category", label: "Κατηγορία", type: "text" },
      { name: "date", label: "Ημερομηνία (π.χ. 18 Ιουνίου 2026)", type: "text" },
      { name: "read_time", label: "Χρόνος ανάγνωσης (π.χ. 5 λεπτά)", type: "text" },
      { name: "author", label: "Συγγραφέας", type: "text" },
      { name: "status", label: "Κατάσταση", type: "select", options: ["published", "draft"] },
    ],
  },
};

const defaultsFor = (kind) =>
  Object.fromEntries(
    KINDS[kind].fields.map((f) => [f.name, f.type === "switch" ? false : f.type === "select" ? f.options[0] : ""])
  );

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Καλωσήρθες!");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 pt-24 pb-16" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-ink text-cream p-8 sm:p-10">
        <p className="eyebrow text-terracotta">Διαχείριση</p>
        <h1 className="font-display text-3xl font-bold mt-3">Σύνδεση</h1>
        <div className="space-y-5 mt-8">
          <div>
            <Label htmlFor="admin-email" className="text-cream/70 text-xs font-mono2 tracking-wider uppercase">
              Email
            </Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-cream/5 border-cream/20 text-cream rounded-none focus-visible:ring-terracotta"
            />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-cream/70 text-xs font-mono2 tracking-wider uppercase">
              Κωδικός
            </Label>
            <div className="relative mt-2">
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type={show ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-cream/5 border-cream/20 text-cream rounded-none pr-10 focus-visible:ring-terracotta"
              />
              <button
                type="button"
                data-testid="admin-password-toggle"
                onClick={() => setShow((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/50 hover:text-cream"
                aria-label="Toggle password"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && (
            <p className="text-sm text-terracotta" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full rounded-full bg-terracottaD hover:bg-cream hover:text-ink text-cream font-medium transition-colors duration-300"
          >
            {busy ? "Σύνδεση…" : "Είσοδος"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const ItemDialog = ({ kind, item, onClose, onSaved }) => {
  const { token } = useAuth();
  const [form, setForm] = useState(() => {
    if (!item) return defaultsFor(kind);
    const base = { ...defaultsFor(kind), ...item };
    if (kind === "tools" && Array.isArray(base.tags)) base.tags = base.tags.join(", ");
    return base;
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form };
    for (const f of KINDS[kind].fields) {
      if (f.type === "number") payload[f.name] = payload[f.name] === "" ? null : Number(payload[f.name]);
      if (f.name === "tags" && typeof payload.tags === "string")
        payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
    }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (item) {
        await api.put(`/admin/content/${kind}/${item.id}`, payload, { headers });
        toast.success("Η καταχώρηση ενημερώθηκε");
      } else {
        await api.post(`/admin/content/${kind}`, payload, { headers });
        toast.success("Η καταχώρηση δημιουργήθηκε");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-cream border-ink/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-ink">
            {item ? "Επεξεργασία" : "Νέα καταχώρηση"} — {KINDS[kind].label}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {KINDS[kind].fields.map((f) => (
            <div key={f.name}>
              {f.type === "switch" ? (
                <div className="flex items-center justify-between border border-ink/15 px-4 py-3">
                  <Label className="text-sm text-ink/80">{f.label}</Label>
                  <Switch
                    data-testid={`field-${f.name}`}
                    checked={!!form[f.name]}
                    onCheckedChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}
                  />
                </div>
              ) : f.type === "select" ? (
                <div>
                  <Label className="text-xs font-mono2 tracking-wider uppercase text-ink/60">{f.label}</Label>
                  <Select value={form[f.name]} onValueChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))}>
                    <SelectTrigger data-testid={`field-${f.name}`} className="mt-1.5 rounded-none border-ink/20 bg-cream">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-cream border-ink/15">
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o === "published" ? "Δημοσιευμένο" : o === "draft" ? "Πρόχειρο" : o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : f.type === "textarea" ? (
                <div>
                  <Label className="text-xs font-mono2 tracking-wider uppercase text-ink/60">{f.label}</Label>
                  <Textarea
                    data-testid={`field-${f.name}`}
                    rows={f.name === "content" ? 7 : 3}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                    className="mt-1.5 rounded-none border-ink/20 bg-cream"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-mono2 tracking-wider uppercase text-ink/60">{f.label}</Label>
                  <Input
                    data-testid={`field-${f.name}`}
                    type={f.type === "number" ? "number" : "text"}
                    required={f.name === "title" || f.name === "name"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                    className="mt-1.5 rounded-none border-ink/20 bg-cream"
                  />
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              data-testid="admin-item-save"
              disabled={busy}
              className="rounded-full bg-ink hover:bg-terracottaD text-cream transition-colors duration-300"
            >
              {busy ? "Αποθήκευση…" : "Αποθήκευση"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="admin-item-cancel"
              className="rounded-full border-ink/25"
            >
              Ακύρωση
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Dashboard = () => {
  const { token, user, logout } = useAuth();
  const [kind, setKind] = useState("publications");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [dialog, setDialog] = useState(null); // null | { item }

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (k = kind) => {
    try {
      const r = await api.get(`/admin/content/${k}`, { headers });
      setItems(r.data);
    } catch {
      toast.error("Αποτυχία φόρτωσης");
    }
  };

  const loadCounts = async () => {
    const entries = await Promise.all(
      Object.keys(KINDS).map((k) => api.get(`/admin/content/${k}`, { headers }).then((r) => [k, r.data.length]).catch(() => [k, 0]))
    );
    setCounts(Object.fromEntries(entries));
  };

  useEffect(() => {
    load(kind);
  }, [kind]);

  useEffect(() => {
    loadCounts();
  }, []);

  const remove = async (item) => {
    if (!window.confirm("Σίγουρα θέλεις να διαγραφεί αυτή η καταχώρηση;")) return;
    try {
      await api.delete(`/admin/content/${kind}/${item.id}`, { headers });
      toast.success("Η καταχώρηση διαγράφηκε");
      load();
      loadCounts();
    } catch {
      toast.error("Αποτυχία διαγραφής");
    }
  };

  return (
    <div className="pt-28 sm:pt-32 pb-24 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow text-terracottaD">Διαχείριση Περιεχομένου</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Γεια σου, {user?.name || "Admin"}</h1>
          </div>
          <Button
            variant="outline"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="rounded-full border-ink/25 gap-2"
          >
            <LogOut className="w-4 h-4" /> Αποσύνδεση
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {Object.entries(KINDS).map(([k, cfg]) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              data-testid={`stat-${k}`}
              className={`text-left p-5 border transition-colors duration-300 ${
                kind === k ? "bg-ink text-cream border-ink" : "bg-sand border-ink/10 hover:border-terracotta"
              }`}
            >
              <span className="font-display text-3xl font-bold">{counts[k] ?? "—"}</span>
              <span className={`block font-mono2 text-[10px] tracking-[0.2em] uppercase mt-2 ${kind === k ? "text-cream/60" : "text-slate2"}`}>
                {cfg.label}
              </span>
            </button>
          ))}
        </div>

        <Tabs value={kind} onValueChange={setKind} className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList className="bg-sand rounded-full p-1">
              {Object.entries(KINDS).map(([k, cfg]) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  data-testid={`tab-${k}`}
                  className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-cream"
                >
                  {cfg.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <Button
              onClick={() => setDialog({ item: null })}
              data-testid="admin-add-btn"
              className="rounded-full bg-terracottaD hover:bg-ink text-cream gap-2 transition-colors duration-300"
            >
              <Plus className="w-4 h-4" /> Νέα καταχώρηση
            </Button>
          </div>

          {Object.keys(KINDS).map((k) => (
            <TabsContent key={k} value={k} className="mt-6">
              <div className="border border-ink/10 bg-sand/50 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-ink/10">
                      <TableHead className="font-mono2 text-[10px] tracking-[0.2em] uppercase">Τίτλος</TableHead>
                      <TableHead className="font-mono2 text-[10px] tracking-[0.2em] uppercase">Κατάσταση</TableHead>
                      <TableHead className="text-right font-mono2 text-[10px] tracking-[0.2em] uppercase">Ενέργειες</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => (
                      <TableRow key={item.id} className="border-ink/10" data-testid={`admin-row-${i}`}>
                        <TableCell className="font-medium max-w-xs truncate">
                          {item[KINDS[k].titleField]}
                          {item.featured && k === "publications" && (
                            <span className="ml-2 font-mono2 text-[9px] tracking-wider uppercase text-terracottaD">★ Προβεβλημένη</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`font-mono2 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 ${
                              item.status === "published" ? "bg-sage/20 text-[#5c7355]" : "bg-ochre/20 text-[#8a5f1e]"
                            }`}
                          >
                            {item.status === "published" ? "Δημοσιευμένο" : "Πρόχειρο"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-2">
                            <button
                              onClick={() => setDialog({ item })}
                              data-testid={`admin-edit-${i}`}
                              className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-ink/20 transition-colors duration-300 hover:bg-ink hover:text-cream"
                              aria-label="Επεξεργασία"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => remove(item)}
                              data-testid={`admin-delete-${i}`}
                              className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-ink/20 transition-colors duration-300 hover:bg-terracotta hover:border-terracotta hover:text-cream"
                              aria-label="Διαγραφή"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-slate2 py-10 font-mono2 text-sm">
                          Καμία καταχώρηση ακόμα.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {dialog && (
        <ItemDialog
          kind={kind}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={() => {
            load();
            loadCounts();
          }}
        />
      )}
    </div>
  );
};

export default function Admin() {
  const { token, loading } = useAuth();

  if (loading) {
    return <div className="min-h-[80vh] flex items-center justify-center font-mono2 text-sm text-slate2 pt-24">Φόρτωση…</div>;
  }
  return token ? <Dashboard /> : <LoginForm />;
}
